// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { ChildFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

enum fields {
    links = "links",
    copyright = "copyright",
}

interface OwnProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.copyright]: string;
    };
}

interface LinkModel {
    fields: {
        overrideTitle: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
    };
}

export interface FooterBottomSectionStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    rightSection?: InjectableCss;
    linkStyles?: LinkProps;
    copyright?: TypographyPresentationProps;
}

export const footerBottomSectionStyles: FooterBottomSectionStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 15px 0;
            gap: 16px 40px;
            flex-wrap: wrap;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", "sm")} {
                align-items: flex-start;
                flex-direction: column;
                flex-wrap: nowrap;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            ${layoutContainer}
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                row-gap: 16px;
                align-items: center;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px 0px;
        `,
    },
    linkStyles: {
        mergeCss: true,
        typographyProps: {
            size: 14,
            lineHeight: "32px",
            weight: 400,
            css: css`
                line-height: 12px;
                letter-spacing: 0;
                &&&& {
                    color: ${color("common.ui3")};
                    &:hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
        css: css`
            &:not(:last-child):after {
                content: "|";
                color: ${color("common.ui1")};
                margin: 0 8px;
            }
        `,
    },
    copyright: {
        size: 14,
        weight: "normal",
        lineHeight: "32px",
        color: "text.main",
        css: css`
            line-height: 12px;
            letter-spacing: 0;
            margin-bottom: 0;

            & > ${TypographyStyle} {
                display: inline-block;
                font-size: inherit;
                color: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
        `,
    },
};

const styles = footerBottomSectionStyles;
const FooterBottomSection = ({ fields: { links, copyright } }: OwnProps) => {
    const linksList = useGetLinks(links, o => o.fields.destination);
    const year = new Date().getFullYear();

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="footer-bottom-section-widget">
            <StyledWrapper {...styles.container}>
                <Typography {...styles.copyright} as="div">
                    &copy; {year} {parse(copyright, parserOptions)}
                </Typography>
                <StyledWrapper {...styles.rightSection}>
                    {links.map((item, index) => (
                        <Link
                            key={item.fields.overrideTitle || linksList[index].url}
                            href={linksList[index].url}
                            target={item.fields.openInNewWindow ? "_blank" : ""}
                            {...styles.linkStyles}
                            aria-label={`${item.fields.overrideTitle}`}
                        >
                            {item.fields.overrideTitle}
                        </Link>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const linksTab = {
    displayName: "Links",
    sortOrder: 2,
};

const copyrightTab = {
    displayName: "Copyright",
    sortOrder: 1,
};

const linkChildFieldDefinition: ChildFieldDefinition[] = [
    {
        name: "overrideTitle",
        editorTemplate: "TextField",
        defaultValue: "",
    },
    {
        name: "destination",
        editorTemplate: "LinkField",
        defaultValue: {
            value: "",
            type: "Url",
        },
        isRequired: true,
    },
    {
        name: "openInNewWindow",
        editorTemplate: "CheckboxField",
        defaultValue: false,
    },
];

const widgetModule: WidgetModule<FieldDefinition> = {
    component: FooterBottomSection,
    definition: {
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.overrideTitle || "";
                },
                defaultValue: [],
                fieldType: "General",
                tab: linksTab,
                fieldDefinitions: [...linkChildFieldDefinition],
            },
            {
                name: fields.copyright,
                displayName: "Company Copyright",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: copyrightTab,
            },
        ],
    },
};

export default widgetModule;
